import { z } from 'zod';
import { amountCents, isoDate, uuid } from './common.js';
import { discipline, registrationMode, TEAM_SIZE_MIN } from './event.js';

export const formatStatus = z.enum(['draft', 'open', 'closed', 'sold_out']);

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((s) => (s ? s : null));

/** Editable format fields (A-01). `name` / `description` are French; EN falls back to them. */
const formatFields = z.object({
  name: z.string().trim().min(1).max(80),
  nameEn: optionalText(80),
  description: optionalText(2000),
  descriptionEn: optionalText(2000),
  discipline,
  distanceKm: z.number().positive().max(9999.9).nullable(),
  elevationM: z.number().int().nonnegative().max(20_000).nullable(),
  startsAt: isoDate.nullable(),
  /** Null = unlimited. */
  capacity: z.number().int().positive().max(100_000).nullable(),
  requiresMedicalDocument: z.boolean(),
  brevoListId: z.number().int().positive().nullable(),
  sortOrder: z.number().int().min(0).max(1000),
  status: formatStatus,
  /** Team format (P-20): sizes required for `team`, null for `individual` (`teamSizeIssue`). */
  registrationMode,
  teamSizeMin: z.number().int().min(TEAM_SIZE_MIN).max(1000).nullable(),
  teamSizeMax: z.number().int().min(TEAM_SIZE_MIN).max(1000).nullable(),
});

export type TeamSizeIssue = 'team_size_min_required' | 'team_size_max_below_min';

/**
 * Team size rules (PRD §4.7): a team format needs a minimum (≥ 2); the maximum is optional
 * (null = unlimited) and never below the minimum. Individual formats carry no sizes. Shared by
 * the back-office form and the API, which checks the merged row on an update.
 */
export function teamSizeIssue(f: {
  registrationMode: 'individual' | 'team';
  teamSizeMin: number | null;
  teamSizeMax: number | null;
}): TeamSizeIssue | null {
  if (f.registrationMode !== 'team') return null;
  if (f.teamSizeMin === null) return 'team_size_min_required';
  if (f.teamSizeMax !== null && f.teamSizeMax < f.teamSizeMin) return 'team_size_max_below_min';
  return null;
}

export const createFormatInput = formatFields
  .extend({
    /** URL key, unique in the event; derived from the FR name when omitted. */
    slug: z
      .string()
      .trim()
      .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/)
      .max(60)
      .optional(),
  })
  .superRefine((f, ctx) => {
    const issue = teamSizeIssue(f);
    if (issue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: issue,
        path: [issue === 'team_size_min_required' ? 'teamSizeMin' : 'teamSizeMax'],
      });
    }
  });
export type CreateFormatInput = z.infer<typeof createFormatInput>;

export const updateFormatInput = formatFields.partial().strict();
export type UpdateFormatInput = z.infer<typeof updateFormatInput>;

/** A tier's window is [startsAt, endsAt); null = open-ended. Issue messages are i18n keys. */
const tierFields = z.object({
  name: z.string().trim().min(1).max(60),
  priceCents: amountCents.max(100_000),
  startsAt: isoDate.nullable(),
  endsAt: isoDate.nullable(),
  /** Stored for the open question on tiers closing on places (PRD §14); checkout ignores it. */
  capacity: z.number().int().positive().max(100_000).nullable(),
});
const endsAfterStart = (t: { startsAt: string | null; endsAt: string | null }) =>
  !t.startsAt || !t.endsAt || new Date(t.startsAt) < new Date(t.endsAt);

export const tierInput = tierFields.refine(endsAfterStart, { message: 'order', path: ['endsAt'] });
export type TierInput = z.infer<typeof tierInput>;

/**
 * "Change the price": the current tier ends at `switchAt` and a new one starts there. Prices
 * never change on a tier an order references (CLAUDE.md), so this is how a price changes.
 */
export const replaceTierInput = tierFields
  .omit({ startsAt: true })
  .extend({ switchAt: isoDate })
  .refine((t) => endsAfterStart({ startsAt: t.switchAt, endsAt: t.endsAt }), {
    message: 'order',
    path: ['endsAt'],
  });
export type ReplaceTierInput = z.infer<typeof replaceTierInput>;

export const adminTier = z.object({
  id: uuid,
  name: z.string(),
  priceCents: amountCents,
  startsAt: isoDate.nullable(),
  endsAt: isoDate.nullable(),
  capacity: z.number().int().nullable(),
  sortOrder: z.number().int(),
  /** Registrations referencing the tier, any status: > 0 = price and dates frozen. */
  registrations: z.number().int(),
  live: z.boolean(),
});
export type AdminTier = z.infer<typeof adminTier>;

export const adminFormat = formatFields.extend({
  id: uuid,
  slug: z.string(),
  /** Places taken now: paid, and pending orders still inside their hold. */
  paid: z.number().int(),
  held: z.number().int(),
  /** Registrations of any status: > 0 = the format can't be deleted. */
  registrations: z.number().int(),
  tiers: z.array(adminTier),
  updatedAt: isoDate,
});
export type AdminFormat = z.infer<typeof adminFormat>;

export const adminFormatsOutput = z.object({ formats: z.array(adminFormat) });
export type AdminFormatsOutput = z.infer<typeof adminFormatsOutput>;
